import { useRef, useCallback } from 'react';
import { FormControl, FormHelperText, Button, Typography, Box, Chip } from '@mui/material';
import type { FieldAdapterProps } from '../../../runtime/forms/field-adapter-registry';
import { resolveTKey, translateRuntimeTKeyOrFallback } from '../../../i18n/resolve-key';
import { buildValidationRules } from '../../../runtime/forms/validation-registry';
import { Controller } from 'react-hook-form';

function getFileName(value: unknown): string | null {
  if (value instanceof File) return value.name;
  if (typeof value === 'string' && value) return value;
  return null;
}

export function FileUploadAdapter(props: FieldAdapterProps) {
  const {
    field,
    state,
    register,
    control,
    formState,
    serverError,
    pending,
    t,
    formValues,
    describedBy,
  } = props;
  const label = t(resolveTKey(field.labelKey));
  const fieldError = formState.errors[field.name]?.message as string | undefined;
  const error = fieldError
    ? fieldError
    : serverError
      ? translateRuntimeTKeyOrFallback(t, serverError)
      : undefined;
  const helpText = field.helpTextKey ? t(resolveTKey(field.helpTextKey)) : undefined;
  const helperText = error ?? helpText;
  const helperId = helperText ? `${field.name}-file-helper` : undefined;
  const ariaDescribedBy = [describedBy, helperId].filter(Boolean).join(' ') || undefined;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rules = buildValidationRules(field, t, formValues);

  const handleClear = useCallback((onChange: (value: unknown) => void) => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const renderSelected = (value: unknown) => {
    const name = getFileName(value);
    if (!name) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%', mt: 1 }}>
        <Chip
          label={name}
          size="small"
          title={name}
          sx={{
            maxWidth: '100%',
            '& .MuiChip-label': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Box>
    );
  };

  if (!control) {
    return (
      <FormControl fullWidth error={Boolean(error)} required={state.required} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {label}
        </Typography>
        <Box
          component="input"
          type="file"
          disabled={state.disabled || pending}
          aria-label={label}
          aria-required={state.required}
          aria-invalid={Boolean(error)}
          aria-describedby={ariaDescribedBy}
          sx={{ maxWidth: '100%' }}
          {...register(field.name, rules)}
        />
        {helperText && (
          <FormHelperText id={helperId} error={Boolean(error)} sx={{ overflowWrap: 'anywhere' }}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <Controller
      control={control}
      name={field.name}
      rules={rules}
      render={({ field: controllerField }) => (
        <FormControl fullWidth error={Boolean(error)} required={state.required} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {label}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={state.disabled || pending}
              component="label"
              htmlFor={`${field.name}-file-input`}
            >
              {t('form:chooseFile')}
            </Button>
            {controllerField.value && (
              <Button
                variant="text"
                size="small"
                disabled={state.disabled || pending}
                onClick={() => handleClear(controllerField.onChange)}
              >
                {t('form:removeFile')}
              </Button>
            )}
          </Box>
          <Box
            component="input"
            id={`${field.name}-file-input`}
            ref={(el: HTMLInputElement | null) => {
              inputRef.current = el;
              controllerField.ref(el);
            }}
            type="file"
            name={controllerField.name}
            disabled={state.disabled || pending}
            aria-label={label}
            aria-required={state.required}
            aria-invalid={Boolean(error)}
            aria-describedby={ariaDescribedBy}
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              controllerField.onChange(e.target.files?.[0] ?? null);
            }}
            onBlur={controllerField.onBlur}
          />
          {renderSelected(controllerField.value)}
          {helperText && (
            <FormHelperText id={helperId} error={Boolean(error)} sx={{ overflowWrap: 'anywhere' }}>
              {helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
